// frontend/src/hooks/usePushNotifications.js
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Clave publica VAPID — la que generamos al principio
const VAPID_PUBLIC_KEY = 'BOlZ7RMpnpvpO79D4Xw7uMt0DCLDaReIBtf7TFchuv6p4PXGrB2vGR8TyzwW7SAWAwQDglE2fB11IIjPhae6ZRA';

/**
 * Convierte la clave publica VAPID de base64url a Uint8Array.
 * El navegador la necesita en este formato para suscribirse.
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

/**
 * Hook para manejar las push notifications del usuario.
 * 
 * Uso en cualquier componente:
 *   const { isSupported, permission, subscribe, unsubscribe } = usePushNotifications();
 */
export function usePushNotifications() {
    const [isSupported, setIsSupported] = useState(false);
    const [permission, setPermission] = useState('default');
    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        // Verificar soporte del navegador
        const supported = 'serviceWorker' in navigator && 'PushManager' in window;
        setIsSupported(supported);

        if (supported) {
            setPermission(Notification.permission);

            // Verificar si ya hay suscripcion activa
            navigator.serviceWorker.ready.then((reg) => {
                reg.pushManager.getSubscription().then((sub) => {
                    setSubscription(sub);
                });
            });
        }
    }, []);

    const subscribe = useCallback(async () => {
        if (!isSupported) return { success: false, reason: 'not_supported' };

        try {
            // Registrar el service worker si no esta registrado
            const registration = await navigator.serviceWorker.register('/service-worker.js');
            await navigator.serviceWorker.ready;

            // Pedir permiso al usuario
            const perm = await Notification.requestPermission();
            setPermission(perm);

            if (perm !== 'granted') {
                return { success: false, reason: 'denied' };
            }

            // Suscribirse al servicio de push
            const sub = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
            });

            // Enviar la suscripcion al backend
            const subJson = sub.toJSON();
            await api.post('/push/subscribe', {
                endpoint: subJson.endpoint,
                p256dh: subJson.keys.p256dh,
                auth: subJson.keys.auth
            });

            setSubscription(sub);
            return { success: true };

        } catch (error) {
            console.error('Error al suscribirse a notificaciones:', error);
            return { success: false, reason: 'error', error };
        }
    }, [isSupported]);

    const unsubscribe = useCallback(async () => {
        if (!subscription) return;

        try {
            const subJson = subscription.toJSON();

            // Eliminar del backend
            await api.delete('/push/unsubscribe', {
                data: {
                    endpoint: subJson.endpoint,
                    p256dh: subJson.keys.p256dh,
                    auth: subJson.keys.auth
                }
            });

            // Desuscribirse en el navegador
            await subscription.unsubscribe();
            setSubscription(null);
            setPermission('default');

        } catch (error) {
            console.error('Error al desuscribirse:', error);
        }
    }, [subscription]);

    return {
        isSupported,
        permission,
        isSubscribed: !!subscription,
        subscribe,
        unsubscribe
    };
}
